import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import { FeatureRow } from './components/FeatureRow';

const sources = [
  {
    labelKey: 'chipQuote',
    active: true,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M7 7h6M7 11h10" />
        <path d="M3 4h18v13H8l-5 4z" />
      </svg>
    ),
  },
  {
    labelKey: 'chipLink',
    active: false,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1.5 1.5" />
        <path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1.5-1.5" />
      </svg>
    ),
  },
  {
    labelKey: 'chipPhoto',
    active: false,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <rect x="3" y="4" width="18" height="16" rx="2.5" />
        <circle cx="8.5" cy="9.5" r="1.6" />
        <path d="M21 16l-5-5L4 20" />
      </svg>
    ),
  },
  {
    labelKey: 'chipList',
    active: false,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
      </svg>
    ),
  },
];

// TODO(SP-669) https://mindfeed.atlassian.net/browse/SP-669 — learn links
// temporarily hidden for launch. To re-enable, import `LearnLinkProps` from
// './components/LearnLink', uncomment this block, and pass
// `learnLinks={learnLinks}` to the <FeatureRow> below.
/*
const learnLinks: LearnLinkProps[] = [
  {
    label: 'See how capture works',
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.2 1 2V18h6v-1.3c0-.8.4-1.5 1-2A7 7 0 0 0 12 2z" />
      </svg>
    ),
  },
  {
    label: 'Save from any app with extensions',
    icon: (
      <svg viewBox="0 0 24 24">
        <rect x="3" y="3" width="18" height="18" rx="3" />
        <path d="M3 9h18" />
      </svg>
    ),
  },
];
*/

const Composer = () => {
  const t = useTranslations();
  return (
    <div className={styles.composer}>
      <div className={styles.header.container}>
        <b className={styles.header.title}>
          {t('pages.home.section10.composerTitle')}
        </b>
        <span className={styles.header.close}>×</span>
      </div>
      <div className={styles.body}>
        <div className={styles.sources.container}>
          {sources.map((source) => (
            <span
              key={source.labelKey}
              className={classNames(
                styles.sources.chip.base,
                source.active
                  ? styles.sources.chip.active
                  : styles.sources.chip.inactive,
              )}
            >
              {source.icon}
              {t(`pages.home.section10.${source.labelKey}`)}
            </span>
          ))}
        </div>
        <div className={styles.input}>
          &ldquo;{t('pages.home.section10.sampleQuote')}&rdquo;
          <span className={styles.cursor} />
        </div>
        <div className={styles.meta.container}>
          <span className={styles.meta.box}>
            <span className={styles.meta.swatch} />
            {t('pages.home.section10.box')}
          </span>
          <span className={styles.meta.save}>
            {t('pages.home.section10.save')}
          </span>
        </div>
      </div>
    </div>
  );
};

export const Section10 = () => {
  const t = useTranslations();
  return (
    <FeatureRow
      eyebrow={t('pages.home.section10.eyebrow')}
      title={t('pages.home.section10.title')}
      description={t('pages.home.section10.description')}
      visual={<Composer />}
    />
  );
};

const styles = {
  composer: classNames(
    'w-full max-w-[430px] overflow-hidden rounded-[18px] border bg-white',
    'border-[#F0F0F0] shadow-[0_14px_36px_rgba(20,30,60,0.07)]',
    'marketing-dark:border-white/10 marketing-dark:shadow-[0_40px_90px_rgba(2,8,30,0.55)]',
  ),
  header: {
    container:
      'flex items-center justify-between border-b border-[#f0f1f4] px-[18px] py-4',
    title: 'text-[15px] font-bold text-mf-off-black-600',
    close:
      'flex h-[26px] w-[26px] items-center justify-center rounded-full bg-[#f4f5f7] text-[15px] text-[#8a8f99]',
  },
  body: 'p-[18px]',
  sources: {
    container: 'mb-[14px] flex flex-wrap gap-2',
    chip: {
      base: 'flex items-center gap-1.5 rounded-full border px-3 py-[7px] text-xs font-semibold [&>svg]:h-[13px] [&>svg]:w-[13px] [&>svg]:stroke-[1.9]',
      active: 'border-[#bcd3f3] bg-[#EBF0F7] text-mf-blue',
      inactive: 'border-[#eceef2] bg-[#f4f5f7] text-[#5b606b]',
    },
  },
  input:
    'min-h-[74px] rounded-xl border border-[#e4e7ee] px-[14px] py-[13px] font-serif text-[15px] italic leading-[1.45] text-mf-off-black-600',
  cursor:
    'ml-px inline-block h-4 w-px translate-y-[3px] animate-pulse bg-mf-blue align-baseline',
  meta: {
    container: 'mt-[13px] flex items-center gap-2',
    // Pastel-yellow box tag in the light theme, coral tint in the dark one.
    box: classNames(
      'flex items-center gap-1.5 rounded-full px-[11px] py-1.5 text-xs font-semibold text-mf-off-black-600',
      'bg-[#FDF6BB] marketing-dark:bg-mf-coral/20',
    ),
    swatch: 'h-2 w-2 rounded-[3px] bg-[#e3c20a] marketing-dark:bg-mf-coral',
    save: 'ml-auto rounded-full bg-mf-blue px-[18px] py-[9px] text-xs font-bold text-white',
  },
};
